import './search-results-list.scss';
import React, {useCallback, useEffect, useState} from 'react';
import {Keyname} from '../../../utils/utils';
import {getNormalizedElementByIndex} from '../../../app-types';
import {
    useSearchPageType,
    useStateDocs,
    useStateExpandedProdDocumentId,
    useStateQueryPending,
    useStateTotalFound,
} from '../../search-hooks';
import {SearchResultItem} from './search-result-item/search-result-item';
import {FixedSizeList} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import {debounce} from 'lodash';
import runtimeContext from '../../../environments/runtime-context';
import {openNewDocument, searchScrollButtonVisibilityChanged} from '../../duck/search-actions';
import {useDispatch} from 'react-redux';
import {ResultItemViewConfig} from '../../../config/config-types';
import {Spinner} from '../../../common/spinner/spinner';
import classNames from 'clsx';

type SearchResultsProps = {
    fetchMore: () => void;
    resultItemViewConfig: ResultItemViewConfig;
    showSidebar: boolean;
};

export const SEARCH_RESULTS_LIST_CLASS = 'search-results-list';

export const SearchResultsListComponent = React.memo(({
                                                          fetchMore,
                                                          resultItemViewConfig,
                                                          showSidebar
                                                      }: SearchResultsProps) => {
    const searchPageType = useSearchPageType();
    const docs = useStateDocs();
    const queryPending = useStateQueryPending();
    const selectedSearchResultItemId = useStateExpandedProdDocumentId();
    const totalFound = useStateTotalFound() || 0;
    const dispatch = useDispatch();
    const visibleDocsLength = docs.allKeys.length;
    const docsLengthIncludingDuplicates = docs.docsLengthIncludingDuplicates;
    const [hasNextPage, setHasNextPage] = useState(false);

    const bottomMargin = 20;
    const getListHeight = useCallback((): number => {
        const mainRowHeight = document.getElementById('hibu_main_row')?.clientHeight;
        const contentHeaderHeight = document.getElementById('search-results-overview-block')?.clientHeight;
        if (mainRowHeight !== undefined && contentHeaderHeight !== undefined) {
            return mainRowHeight - contentHeaderHeight - bottomMargin;
        } else {
            // fallback to hardcoded value just in case
            return 385;
        }
    }, []);

    useEffect(() => {
            if (docsLengthIncludingDuplicates < totalFound) {
                setHasNextPage(true);
            }
        },
        [totalFound, docsLengthIncludingDuplicates]
    );

    const [listHeight, setListHeight] = useState<number>(getListHeight);
    useOnResize(() => setListHeight(getListHeight));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setListHeight(getListHeight);
    });

    /**
     * Handles arrow up and arrow down buttons when scrolling search results.
     * Updates the current scroll top by plus/minus search result item height
     * @param event a keyboard event
     */
    const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (docs.allKeys.length === 1) {
            return;
        }

        // if no search result item is selected, we don't change anything when pressing arrow keys
        if (!selectedSearchResultItemId) {
            return;
        }

        const currentDocIndex = docs.index[selectedSearchResultItemId];
        switch (event.key) {
            case Keyname.ARROW_UP: {
                event.preventDefault();
                if (currentDocIndex !== 0) {
                    dispatch(openNewDocument(getNormalizedElementByIndex(docs, currentDocIndex - 1)));
                }
                break;
            }
            case Keyname.ARROW_DOWN: {
                event.preventDefault();
                const nextDocIndex = currentDocIndex + 1;
                if (nextDocIndex !== docs.allKeys.length) {
                    dispatch(openNewDocument(getNormalizedElementByIndex(docs, nextDocIndex)));
                }
                break;
            }
        }
    };

    const onScroll = () => {
        const container = runtimeContext.document().getElementsByClassName(SEARCH_RESULTS_LIST_CLASS)?.[0];
        if (!container) {
            console.trace('search container is undefined');
            return;
        }
        dispatch(searchScrollButtonVisibilityChanged(searchPageType, container.scrollTop > 150));
    };

    if (!docs || !docs.allKeys.length) {
        return null;
    }

    const fetchMoreItems = () => {
        if (hasNextPage) {
            fetchMore();
        }
        return Promise.resolve();
    };


    return (
        <div id={'hibu_result_list_container'} className={classNames(showSidebar && 'active')}>
            {queryPending && docs && !visibleDocsLength && <Spinner key="results spinner"/>}
            <InfiniteLoader
                isItemLoaded={(index) => !hasNextPage || index < visibleDocsLength} // Function responsible for tracking the loaded state of each item.
                itemCount={totalFound} // Number of rows in list; can be arbitrary high number if actual number is unknown.
                loadMoreItems={fetchMoreItems}
            >
                {({onItemsRendered, ref}) => (
                    <FixedSizeList
                        className={SEARCH_RESULTS_LIST_CLASS}
                        onItemsRendered={onItemsRendered}
                        ref={ref}
                        itemCount={visibleDocsLength}
                        height={listHeight}
                        itemSize={125}
                        itemData={{
                            docs,
                            onKeyDown,
                            resultItemViewConfig
                        }}
                        onScroll={onScroll}
                    >
                        {SearchResultItem}
                    </FixedSizeList>
                )}
            </InfiniteLoader>
        </div>
    );
});

function useOnResize(onWindowResized: (width, height) => void, delay = 500) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onResize = useCallback(
        debounce(() => {
            onWindowResized(window.innerWidth, window.innerHeight);
        }, delay),
        []
    );

    useEffect(() => {
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [onResize]);
}
